<script setup>
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  templates: { type: Array, default: () => [] },
});

const emit = defineEmits(['edit', 'archive']);
const { t } = useI18n();

const statusClass = status => ({
  'bg-n-teal-9/10 text-n-teal-11': status === 'active',
  'bg-n-slate-9/10 text-n-slate-11': status === 'archived',
});

const typeLabel = type =>
  type === 'message'
    ? t('CAMPAIGN.EMAIL.TEMPLATES.TYPE.MESSAGE')
    : t('CAMPAIGN.EMAIL.TEMPLATES.TYPE.EMAIL');

const statusLabel = status =>
  status === 'archived'
    ? t('CAMPAIGN.EMAIL.TEMPLATES.STATUS.ARCHIVED')
    : t('CAMPAIGN.EMAIL.TEMPLATES.STATUS.ACTIVE');
</script>

<template>
  <section
    class="overflow-hidden border-y border-n-weak sm:rounded-lg sm:border"
  >
    <table class="hidden w-full border-collapse text-left text-sm md:table">
      <thead class="bg-n-alpha-2 text-xs uppercase text-n-slate-10">
        <tr>
          <th class="px-4 py-3 font-medium">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.TEMPLATE') }}
          </th>
          <th class="px-4 py-3 font-medium">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.TYPE') }}
          </th>
          <th class="px-4 py-3 font-medium">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.SENDER') }}
          </th>
          <th class="px-4 py-3 font-medium">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.STATUS') }}
          </th>
          <th class="px-4 py-3 text-right font-medium">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="template in templates"
          :key="template.id"
          class="border-t border-n-weak hover:bg-n-alpha-1"
        >
          <td class="max-w-sm px-4 py-3">
            <p class="truncate font-medium text-n-slate-12">
              {{ template.name }}
            </p>
            <p class="truncate text-xs text-n-slate-10">
              {{
                template.subject ||
                template.message ||
                t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.NO_SUBJECT')
              }}
            </p>
          </td>
          <td class="px-4 py-3 text-n-slate-11">
            {{ typeLabel(template.template_type) }}
          </td>
          <td class="max-w-48 truncate px-4 py-3 text-n-slate-11">
            {{ template.sender_name || '-' }}
          </td>
          <td class="px-4 py-3">
            <span
              class="rounded-md px-2 py-1 text-xs font-medium"
              :class="statusClass(template.status)"
            >
              {{ statusLabel(template.status) }}
            </span>
          </td>
          <td class="px-4 py-3">
            <div class="flex justify-end gap-1">
              <Button
                size="sm"
                variant="ghost"
                color="slate"
                icon="i-lucide-pencil"
                :label="t('CAMPAIGN.EMAIL.TEMPLATES.EDIT')"
                @click="emit('edit', template)"
              />
              <Button
                v-if="template.status !== 'archived'"
                size="sm"
                variant="ghost"
                color="ruby"
                icon="i-lucide-archive"
                :label="t('CAMPAIGN.EMAIL.TEMPLATES.ARCHIVE')"
                @click="emit('archive', template)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="divide-y divide-n-weak md:hidden">
      <article v-for="template in templates" :key="template.id" class="p-4">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="truncate text-sm font-medium text-n-slate-12">
              {{ template.name }}
            </h3>
            <p class="mt-1 line-clamp-2 text-xs text-n-slate-10">
              {{
                template.subject ||
                template.message ||
                t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.NO_SUBJECT')
              }}
            </p>
          </div>
          <span
            class="shrink-0 rounded-md px-2 py-1 text-xs font-medium"
            :class="statusClass(template.status)"
          >
            {{ statusLabel(template.status) }}
          </span>
        </div>
        <div
          class="mt-3 flex items-center justify-between gap-3 border-t border-n-weak pt-3"
        >
          <span class="text-xs text-n-slate-10">
            {{
              template.sender_name ||
              t('CAMPAIGN.EMAIL.TEMPLATES.TABLE.NO_SENDER')
            }}
          </span>
          <div class="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-pencil"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.EDIT')"
              @click="emit('edit', template)"
            />
            <Button
              v-if="template.status !== 'archived'"
              size="sm"
              variant="ghost"
              color="ruby"
              icon="i-lucide-archive"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.ARCHIVE')"
              @click="emit('archive', template)"
            />
          </div>
        </div>
      </article>
    </div>
  </section>
</template>
